import type { ChangeEvent, Dispatch, FC, SetStateAction } from "react";

import { Stack } from "@mui/material";
import { styled } from "@mui/system";

import SignatureIcon from "../resources/SignatureIcon.svg";
import CustomButton from "./CustomButton";

import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showFlashMessageWithTimeout } from "../services/actions/flashMessage-actions";

const ImagePreview = styled("img")({
  width: 30,
  height: 30,
  borderRadius: 8,
  objectFit: "cover",
  marginLeft: 10,
});

interface SignatureIconSelectProps {
  logo?: string;
  setLogo: Dispatch<SetStateAction<string | undefined>>;
  onValidationChange?: (isValid: boolean) => void;
}

export const SignatureIconSelect: FC<SignatureIconSelectProps> = ({
  logo,
  setLogo,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Maximum file size: 100 KB (server limit is 102400 bytes)
  const MAX_FILE_SIZE = 100 * 1024; // 100 KB in bytes

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target || !event.target.files) {
      return;
    }

    const file = event.target.files[0];
    if (file) {
      // Validate file size before reading
      if (file.size > MAX_FILE_SIZE) {
        const maxSizeKB = Math.round(MAX_FILE_SIZE / 1024);
        const actualSizeKB = Math.round(file.size / 1024);

        showFlashMessageWithTimeout(
          dispatch,
          t("errors.logoFileTooLarge", {
            maxSize: maxSizeKB,
            actualSize: actualSizeKB,
          }),
          "error",
        );

        // Reset the file input
        event.target.value = "";
        onValidationChange?.(false);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          setLogo(reader.result);
          // Notify parent that validation succeeded
          onValidationChange?.(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Stack
      direction='row'
      sx={{ alignItems: "center", gap: 1.5, boxSizing: "border-box" }}
    >
      <input
        accept='image/*'
        style={{ display: "none" }}
        id='upload-logo'
        type='file'
        onChange={handleFileChange}
      />
      <ImagePreview src={logo ?? SignatureIcon} alt='Logo preview' />
      <label
        htmlFor='upload-logo'
        style={{
          maxHeight: "50px",
        }}
      >
        <CustomButton
          title={t("choose")}
          onClick={() => {}}
          size='small'
          style={{
            padding: "0 8px",
          }}
        />
      </label>
    </Stack>
  );
};
